// TraceWorkFeed — the semantic reading of one trace: the X-ray extractors'
// ops rendered as a turn-ordered feed of typed rows, with unclaimed tool
// calls kept visible as residual interstitials. The sibling of the Context
// (turn-by-turn) reading in the trace-detail panel — same trace, re-bucketed
// from requests into operations. Pure presentation: every fact shown here is
// the tracers' deterministic output (see lib/traceSemanticFeed.js).
//
// Layout contract for the narrow detail panel: ONE flat rail, read as a
// timeline, on THREE fixed columns — a 16px spine gutter, a domain lane, and
// the content. Every op row states its domain in the lane, single or grouped,
// so titles align in one scannable column the whole way down; prompt and reply
// beats span the lane because they are the episode's boundaries rather than
// typed work.
//
// The rail is the timeline and carries everything that is about TIME. The spine
// runs continuous — solid with a node per real operation, dotted through
// unclassified stretches. Capture instants cross it as TICKS, at rung
// boundaries and nowhere else, so the turns a request first carried sit between
// two ticks rather than each repeating a label in a column beside them.
//
// A boundary is a ROW of its own, and it holds the whole of what is true about
// the instant it names: the tick, the time — printed at the lane's left edge, in
// the same column as the domain tags, so the page has one column of labels — and
// beside it the run of marks for what the ENDPOINT was doing in the window that
// instant opens. That row is also the one control that opens the window. Every
// mark a reader can see therefore belongs to something they can open, which is
// what the per-row distribution could not offer: it drew identical marks on
// every row of a stretch while exactly one of them could be expanded.
//
// Rows group into EPISODES,
// each headed by its prompt beat: the prompt sticks to the top of the panel
// scroll while its episode's ops go by, so the reader always knows which
// intent they're inside, and closes with the agent's reply as the episode's
// outcome (intent → work → answer). Harness notifications that arrive as user
// turns are NOT episodes — they render as minor "Notice" beats. File work
// groups BY FILE: every read/edit/write on a path stacks into one
// reconstruction, rendered with the same gutter/provenance rail as the Files
// X-ray (AnnotatedFileBody).
//
// Because the feed AGGREGATES — one file's whole op stack becomes one reading —
// every expanded payload carries its own evidence: a basis strip saying what
// kind of knowledge it is, and a source strip enumerating the turns it came
// from. Without that pair the merge is unverifiable by eye, and the reader has
// no way to get from a line of content back to the call that produced it.
import { Fragment, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { manifestRowBody, manifestRowTitle } from "../lib/manifestOpRow.js";
import { findScrollParent, landOnRow } from "../lib/scrollParent.js";
import { topVisibleIndex } from "../lib/virtualWindow.js";
import { turnFocusInSpan } from "../lib/traceTurnFocus.js";
import {
  buildSemanticFeed,
  residualSummary,
  rowTurnSpan,
  feedDomains,
} from "../lib/traceSemanticFeed.js";
import { reconstructFile } from "../lib/traceFileTree.js";
import { pageDoc } from "../lib/traceWebResearch.js";
import { opObservedDoc, logObsDoc, targetDescDoc, targetCommentDocs, targetDiffDocs } from "../lib/traceForgeOps.js";
import { issueDescDoc, issueCommentDocs } from "../lib/traceLinearOps.js";
import { readToolCall, readToolCallHead, readTurn } from "../lib/traceToolCall.js";
import { fileBasis, observedBasis, payloadSources } from "../lib/traceFeedBasis.js";
import { useTurnClock } from "../lib/useTurnClock.js";
import { artifactOpsForTrace } from "../lib/artifactTraceFeed.js";
import { listBehaviourArtifacts, subscribeBehaviourArtifacts } from "../lib/behaviourArtifactStore.js";
import { useEndpointCorroboration, SPAN_EVENT_LIMIT } from "../lib/useEndpointCorroboration.js";
import {
  corroborationDigest,
  corroborationGroups,
  kindGlyph,
  windowBoundaries,
  windowMarks,
  windowStretches,
} from "../lib/traceEndpointCorroboration.js";
import { hasCandidates, matchWindowEvents, windowCandidates } from "../lib/traceEndpointMatch.js";
import { windowEventRows } from "../lib/traceEndpointEvents.js";
import { AnnotatedFileBody, DiffRows, ResponseBlock, RecallConsultationDetail } from "./tracePayloads.jsx";
import HoverTooltip from "./HoverTooltip.jsx";

// Type scale, anchored to the CONTEXT reading in the same panel (12px mono,
// 11px prose). The feed sat a notch under it on every tier at once, which read
// as a subordinate widget rather than the other half of one surface.
const TITLE_CLASS = "text-[12px] leading-[1.45]";
const META_CLASS = "text-[11px] leading-[1.3]";
const MONO_CLASS = "font-mono text-[12px] leading-[1.5]";
// Split base from color so a tone override can't lose a specificity race with
// the default — both emit a `color` rule and class order would not decide it.
const EYEBROW_BASE = "text-[10px] uppercase tracking-[0.07em] leading-[1.2]";
const EYEBROW_CLASS = `${EYEBROW_BASE} text-[color:var(--eyebrow)]`;
const WARN_CLASS = "text-[color:var(--red,#c0392b)]";
const PRE_LINE_CAP = 20;
const SOURCE_CHIP_CAP = 8;

// STRICT INDENT INVARIANT: exactly one step exists in this feed. A member line
// takes it, and so does a payload — but never both, whatever the nesting depth.
// A payload's left edge therefore never encodes how deep its row happened to
// sit. It used to: a member's payload was indented past the member line, which
// was itself indented inside a group, so the very same file reconstruction
// landed at 14px when its run held one file and 28px when it held two. Nothing
// about the content differed; only whether a sibling existed.
const INDENT = "pl-[14px]";

// The timeline gutter and the domain lane: spine x-position, node geometry and
// lane width share these so every row variant lines up on the same verticals.
// The lane LABEL truncates rather than wrapping — a runtime lens can name a
// domain anything, and a two-line label would break the one-row-one-line
// rhythm.
//
// 64px, because a ROW's lane holds ONE thing: the domain. It was widened to 100
// to fit a clock reading and a cluster of endpoint glyphs on a second line, and
// both have since left the row entirely for the rail's own boundary rows — so
// the 36px is back with the content column, which is where a narrow panel needs
// it. The lane column is shared, not duplicated: a boundary's time prints in the
// same 64px, which is what puts the times and the domain tags in one column.
// 64 is what the longest built-in label measures at 10px uppercase with 0.07em
// tracking (computed from the type scale, not observed): "RESEARCH" and
// "ARTIFACT" come to ~50px, and a runtime lens's longer name truncates, as it
// did before.
const GUTTER_COLS = "grid-cols-[16px_64px_minmax(0,1fr)]";
// The same two verticals, for the rail's boundary rows. A boundary is not a row
// of the feed — it is the instant BETWEEN two of them — so it lays itself out
// with flex on these widths rather than becoming a fourth kind of grid child.
// Keep the three literals in step: the whole point is that a boundary's time
// starts exactly where a row's domain tag does.
const SPINE_COL = "w-[16px]";
const LANE_COL = "w-[64px]";
// The line box a boundary's tick, time and marks all centre on. 14px is the
// 10px clock reading at its 1.4 leading (computed from the type scale, not
// observed); the crossbar and the marks are centred against it.
const BOUNDARY_LINE = "h-[14px]";
// Side and alpha per magnitude step (see markWeight). Square, and never round:
// a turn node is a filled or hollow CIRCLE, and shape is the one difference a
// reader registers before size or tone, so an event mark can never be mistaken
// for a beat of the conversation however long the run gets.
const MARK_SIDE = { 1: 2, 2: 3, 3: 4 };
const MARK_ALPHA = { 1: 45, 2: 70, 3: 100 };
// How many correspondences earn a line before the rest go to the expansion. A
// match is meant to be the rare, specific thing on the page; three of them
// stacked under one row is the clutter this reading replaced.
const MATCH_LINE_CAP = 2;

// The open trace's own timestamp, for the shared renderers' "observed at"
// lines. Contexted rather than prop-drilled through every row variant.
const TraceTimeContext = createContext(undefined);
// The trace's turns, for the residual reader: an unclaimed call's content is
// recovered from the raw blocks on demand rather than carried on its feed op
// (see lib/traceToolCall.js for why).
const TraceTurnsContext = createContext(null);
// How any evidence affordance — a source chip, a line-history version — asks for
// its turn. It resolves INLINE, below the row that asked: this reading is
// self-contained, and a click that swapped the panel out to the Context reading
// cost the reader their place in the feed to answer a question the feed already
// holds the material for. `{ open, request }`, bound per row by the feed.
const TurnPreviewContext = createContext(null);
// The explicit pivot out to the Context reading, reachable only from inside a
// turn preview. Contexted so a preview can offer it wherever it renders.
const JumpToTurnContext = createContext(null);
// Approximate wall-clock for each turn, resolved as rows scroll into view (see
// lib/useTurnClock.js). Null when the trace has no session to date against.
const TurnClockContext = createContext(null);
// What the ENDPOINT was doing during a turn window. Null when the trace names no
// endpoint — and the whole reading below has to survive that, because
// corroboration is an addition to the turn reading and never a part of it.
const EndpointCorroborationContext = createContext(null);

// A rung's capture time, as a clock reading. Deliberately time-of-day only: the
// interval is approximate to begin with (an upper bound on when the turn
// happened), and a full date beside every row would spend the width claiming a
// precision the reading does not have.
function clockLabel(ts) {
  if (!Number.isFinite(ts)) return null;
  return new Date(ts).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

// Seconds, and ONLY for an endpoint event. A turn's reading is an interval and
// printing seconds on it would claim a precision it does not have; an event has
// a real captured instant, and when a reader is looking at a name match the
// second is the thing they want to line up against everything else.
function eventClockLabel(ts) {
  if (!Number.isFinite(ts)) return null;
  return new Date(ts).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function tailPath(path, segs = 3) {
  if (!path || typeof path !== "string") return path || "";
  const parts = path.split(/[/\\]/).filter(Boolean);
  if (parts.length <= segs) return path;
  return `…/${parts.slice(-segs).join("/")}`;
}

function clip(s, n) {
  const t = String(s || "");
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}

// One-line reading of a single op, per domain: what an administrator scans.
// (Files never reach this — they group by path and use fileDigest instead.)
function summarize(item) {
  const op = item.op || {};
  switch (item.domain) {
    case "research":
      return op.kind === "search"
        ? { title: `searched “${clip(op.query, 90)}”`, meta: `${op.resultCount} results` }
        : { title: `fetched ${clip(op.url, 90)}`, meta: op.host || null };
    case "forge": {
      const target = [op.res, op.num ? `#${op.num}` : null].filter(Boolean).join(" ");
      return {
        title: `${op.create ? "created" : op.kind === "write" ? "wrote" : op.kind === "push" ? "pushed" : op.kind === "browse" ? "browsed" : "read"} ${target || op.repo || "forge"}`,
        meta: op.title || op.repo || null,
      };
    }
    case "linear":
      return {
        title: `${op.create ? "created" : op.kind === "write" ? "wrote" : "read"} ${op.issue || "issues"}`,
        meta: op.title || null,
      };
    case "grafana":
      return { title: `${op.engine}: ${clip(op.target || op.query || "query", 80)}`, meta: op.instance || null };
    case "query":
      return {
        title: `${op.engine} → ${clip(op.target || "query", 60)}`,
        meta: op.instance || null,
      };
    case "recall":
      return op.kind === "docs"
        ? { title: `docs: ${clip((op.entities || []).join(", "), 80)}`, meta: null }
        : { title: `recalled “${clip(op.request, 90)}”`, meta: op.view?.topics?.length ? `${op.view.topics.length} topics` : null };
    case "skill":
      return { title: `/${op.name}`, meta: op.args ? clip(op.args, 80) : null };
    case "artifact":
      return { title: `published ${op.title || tailPath(op.path)}`, meta: op.url || null };
    case "code":
      // Code-mode leftovers: cells whose work no domain claimed, and the
      // wait/stdin plumbing around cells that DID unwrap. A cell with a
      // readable inner command leads with it — the command is what the cell
      // did; "a cell ran" is what it was.
      if (op.kind === "poll") return { title: `polled cell ${op.cellId}`, meta: op.status || null };
      if (op.kind === "stdin") return { title: "sent input to a running cell", meta: null };
      if (op.command) {
        return { title: `cell · ${clip(op.command.split("\n")[0], 90)}`, meta: op.ok === false ? "failed" : null };
      }
      return {
        title: `ran a code cell · ${op.lines} line${op.lines === 1 ? "" : "s"}`,
        meta: op.tools?.length ? `calls ${op.tools.join(", ")}` : op.ok === false ? "failed" : null,
      };
    default:
      // Manifest lenses. The logic is in src/lib/manifestOpRow.js — pure, and
      // testable without mounting a feed.
      return manifestRowTitle(op);
  }
}

// One-line digest of a same-domain run, for the collapsed group header.
function digest(domain, items) {
  const n = items.length;
  if (domain === "files") {
    const kinds = { read: 0, edit: 0, write: 0 };
    const paths = new Set();
    let plus = 0;
    let minus = 0;
    for (const it of items) {
      kinds[it.op.kind] = (kinds[it.op.kind] || 0) + 1;
      paths.add(it.op.path);
      plus += it.op.plus || 0;
      minus += it.op.minus || 0;
    }
    const parts = ["read", "edit", "write"].filter((k) => kinds[k]).map((k) => `${kinds[k]} ${k}${kinds[k] > 1 ? "s" : ""}`);
    const delta = plus + minus > 0 ? ` · +${plus} −${minus}` : "";
    return `${parts.join(" · ")} across ${paths.size} file${paths.size > 1 ? "s" : ""}${delta}`;
  }
  if (domain === "research") {
    const searches = items.filter((i) => i.op.kind === "search").length;
    const fetches = n - searches;
    return [searches ? `${searches} search${searches > 1 ? "es" : ""}` : null, fetches ? `${fetches} page${fetches > 1 ? "s" : ""}` : null]
      .filter(Boolean)
      .join(" · ");
  }
  if (domain === "code") {
    const counts = { cell: 0, poll: 0, stdin: 0 };
    for (const it of items) counts[it.op.kind] = (counts[it.op.kind] || 0) + 1;
    return [
      counts.cell ? `${counts.cell} cell${counts.cell > 1 ? "s" : ""}` : null,
      counts.poll ? `${counts.poll} poll${counts.poll > 1 ? "s" : ""}` : null,
      counts.stdin ? `${counts.stdin} stdin send${counts.stdin > 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(" · ");
  }
  return `${n} ops`;
}

// One file's aggregate line: op-kind counts plus the net ± (or the read
// size when the file was only read).
function fileDigest(items) {
  const kinds = { read: 0, edit: 0, write: 0 };
  let plus = 0;
  let minus = 0;
  let readLines = 0;
  for (const it of items) {
    kinds[it.op.kind] = (kinds[it.op.kind] || 0) + 1;
    plus += it.op.plus || 0;
    minus += it.op.minus || 0;
    readLines = Math.max(readLines, it.op.readLines || 0);
  }
  const parts = ["read", "edit", "write"].filter((k) => kinds[k]).map((k) => `${kinds[k]} ${k}${kinds[k] > 1 ? "s" : ""}`);
  let out = parts.join(" · ");
  if (plus + minus > 0) out += ` · +${plus} −${minus}`;
  else if (readLines) out += ` · ${readLines} line${readLines === 1 ? "" : "s"}`;
  return out;
}

// A run's member list. Files group BY PATH across the whole run — every op
// on a file stacks into one member (ordered by first touch) so its history
// reconstructs in one place. Other domains stay one-op-per-member.
function groupMembers(domain, items) {
  if (domain !== "files") return items.map((item) => ({ items: [item] }));
  const byPath = new Map();
  for (const item of items) {
    const key = item.op.path;
    if (!byPath.has(key)) byPath.set(key, { items: [] });
    byPath.get(key).items.push(item);
  }
  return [...byPath.values()];
}

function memberSpan(items) {
  const idxs = items.map((it) => it.turnIndex).filter((v) => v != null);
  if (!idxs.length) return null;
  return { start: Math.min(...idxs), end: Math.max(...idxs) };
}

// The one labelled-row primitive: LABEL │ value, with an optional right-hand
// outcome. Every structured body in the feed is built from it — a manifest
// lens's steps, an unclaimed call's input and result, a query as authored — so
// structure comes back as alignment rather than as another nested box.
function KvRow({ label, children, right = null, tone = null }) {
  return (
    <div className="flex min-w-0 items-baseline gap-[9px] px-[9px] py-[2px]">
      <span className={`${EYEBROW_CLASS} w-[62px] shrink-0`}>{label}</span>
      <span className={`min-w-0 flex-1 whitespace-pre-wrap break-words ${MONO_CLASS} ${tone === "soft" ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>
        {children}
      </span>
      {right ? <span className={`${META_CLASS} shrink-0 text-[color:var(--muted)]`}>{right}</span> : null}
    </div>
  );
}

// One contributing turn, as a locator. The bordered chip is the affordance the
// bare grey "T114" never was — a reader had to guess it was clickable, and the
// note is what makes a stack of them readable ("that line came from turn 116's
// edit") instead of a row of opaque numbers. Clicking opens the turn INLINE; it
// never navigates.
function SourceChip({ turn, note }) {
  const preview = useContext(TurnPreviewContext);
  const active = preview?.open === turn;
  const inner = (
    <>
      <span className="font-mono text-[11px] tabular-nums">{`T${turn}`}</span>
      {note ? <span className={`${META_CLASS} normal-case tracking-normal text-[color:var(--muted-soft)]`}>{note}</span> : null}
    </>
  );
  const base = "inline-flex items-baseline gap-[5px] rounded-[2px] border px-[6px] py-[1px]";
  if (!preview) {
    return <span className={`${base} border-[color:var(--line-soft)] text-[color:var(--muted)]`}>{inner}</span>;
  }
  return (
    <button
      type="button"
      onClick={() => preview.request(turn)}
      className={[
        base,
        "cursor-pointer transition-colors",
        active
          ? "border-[color:var(--line-strong)] bg-[color:var(--control-bg)] text-[color:var(--ink)]"
          : "border-[color:var(--line-soft)] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)]",
      ].join(" ")}
    >
      {inner}
    </button>
  );
}

// One turn of the trace, read back in place: the raw blocks as they were
// captured, in the same labelled-row vocabulary as everything else. This is what
// lets a reader prove where an aggregated payload came from without leaving the
// feed — the question "which call produced this line" is answered next to the
// line, not in another view.
function TurnPreview({ turnIndex }) {
  const turns = useContext(TraceTurnsContext);
  const onOpenInContext = useContext(JumpToTurnContext);
  const read = useMemo(() => readTurn(turns, turnIndex, undefined, { results: true }), [turns, turnIndex]);
  if (!read) return null;
  return (
    // Flush: the caller owns the indent, because a preview opened from a row
    // takes the feed's one step while one opened inside a frame is already
    // within it.
    <div className="min-w-0 pt-[2px]">
      <div className="min-w-0 rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]">
        <div className={`flex min-w-0 items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[4px] ${EYEBROW_CLASS}`}>
          <span className="shrink-0 whitespace-nowrap">{`Turn ${turnIndex}`}</span>
          <span className={`${META_CLASS} min-w-0 flex-1 normal-case tracking-normal text-[color:var(--muted)]`}>
            {`${read.role} · ${read.own} block${read.own === 1 ? "" : "s"} as captured`}
          </span>
          {/* The pivot to the Context reading stays available, but only as an
              explicit choice — never as the side effect of clicking evidence. */}
          {onOpenInContext ? (
            <button
              type="button"
              onClick={() => onOpenInContext(turnIndex)}
              className={`${META_CLASS} shrink-0 cursor-pointer normal-case tracking-normal text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--ink)]`}
            >
              Open in Context
            </button>
          ) : null}
        </div>
        {read.blocks.length ? (
          read.blocks.map((b, i) => (
            <KvRow
              key={i}
              label={b.label}
              tone={b.failed ? null : "soft"}
              right={[
                b.result ? (b.missing ? "no result in the trace" : [`T${b.turnIndex}`, b.via, b.note].filter(Boolean).join(" \u00b7 ")) : null,
                b.clipped ? `+${b.clipped} chars` : null,
                b.failed ? "error" : null,
              ].filter(Boolean).join(" \u00b7 ") || null}
            >
              {b.text || "—"}
            </KvRow>
          ))
        ) : (
          <KvRow label="Empty" tone="soft">No content was recorded for this turn.</KvRow>
        )}
      </div>
    </div>
  );
}

// Every expanded payload, in one frame: a BASIS strip on top (what kind of
// knowledge this is), the body, and a SOURCE strip on the bottom (the turns it
// was built from). One contract across every domain — the file reconstruction,
// the query response, the unclaimed call — so a reader learns to read the
// evidence once. Two file renderings used to sit behind the same chevron with
// nothing to distinguish them: an anchored reconstruction with real line
// numbers, and a hunk that could not be placed at all. Same chrome, opposite
// epistemic status, no label.
function PayloadFrame({ strips = [], sources = [], children }) {
  const shown = sources.slice(0, SOURCE_CHIP_CAP);
  const hidden = sources.length - shown.length;
  const [ownPreview, setOwnPreview] = useState(null);
  const toggleOwn = (turn) => setOwnPreview((cur) => (cur === turn ? null : turn));
  return (
    // data-payload-frame marks the one element the strict indent invariant is
    // measured against: exactly one INDENT step may sit between a row and its
    // frame, whatever the nesting depth (pinned in TraceWorkFeed.test.jsx).
    <div data-payload-frame="" className="min-w-0 rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)]">
      {strips.map((s, i) => (
        <div
          key={i}
          className={`flex min-w-0 items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[4px] ${EYEBROW_BASE} ${s.tone === "warn" ? WARN_CLASS : "text-[color:var(--eyebrow)]"}`}
        >
          <span className="shrink-0 whitespace-nowrap">{s.label}</span>
          {s.detail ? (
            <span className={`${META_CLASS} min-w-0 flex-1 normal-case tracking-normal text-[color:var(--muted)]`}>{s.detail}</span>
          ) : null}
        </div>
      ))}
      {children}
      {shown.length ? (
        // A frame owns its OWN preview so a source turn opens directly beneath
        // the chips, inside the evidence it explains. Deferring to the row-level
        // provider put it at the end of the whole row, which on a row with
        // several expanded files is far enough from the chip to read as broken.
        // The state lives here, so collapsing the payload closes it for free.
        <TurnPreviewContext.Provider value={{ open: ownPreview, request: toggleOwn }}>
          <div className={`flex min-w-0 flex-wrap items-baseline gap-[6px] border-t border-[color:var(--line-soft)] px-[9px] py-[4px] ${EYEBROW_CLASS}`}>
            <span className="shrink-0">Source</span>
            {shown.map((s, i) => (
              <SourceChip key={`${s.turn}:${i}`} turn={s.turn} note={s.note} />
            ))}
            {hidden > 0 ? (
              <span className={`${META_CLASS} normal-case tracking-normal text-[color:var(--muted-soft)]`}>{`+${hidden} more`}</span>
            ) : null}
          </div>
          {ownPreview != null ? (
            <div className="min-w-0 border-t border-[color:var(--line-soft)] px-[9px] pb-[5px]">
              <TurnPreview turnIndex={ownPreview} />
            </div>
          ) : null}
        </TurnPreviewContext.Provider>
      ) : null}
    </div>
  );
}

function MonoBlock({ text, maxLines = PRE_LINE_CAP }) {
  const [showAll, setShowAll] = useState(false);
  if (!text) return null;
  const lines = String(text).split("\n");
  const visible = showAll ? lines : lines.slice(0, maxLines);
  const hidden = lines.length - visible.length;
  return (
    <div className="min-w-0">
      <pre className={`m-0 min-w-0 whitespace-pre-wrap break-words rounded-[2px] bg-[color:var(--control-bg)] px-[7px] py-[5px] ${MONO_CLASS} text-[color:var(--ink)]`}>
        {visible.join("\n")}
      </pre>
      {(hidden > 0 || showAll) && lines.length > maxLines ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-[1px] font-mono text-[11px] text-[color:var(--muted)] cursor-pointer hover:text-[color:var(--ink)]"
        >
          {showAll ? "collapse" : `+${hidden} more lines`}
        </button>
      ) : null}
    </div>
  );
}

function FilePayload({ items }) {
  const requestTurn = useContext(TurnPreviewContext)?.request;
  const { rec, turnMap } = useMemo(() => {
    const ops = items.map((it) => ({ ...it.op, promptId: it.turnIndex }));
    const map = new Map();
    for (const it of items) {
      if (it.turnIndex != null) map.set(it.turnIndex, { summary: `Turn ${it.turnIndex}: ${it.op.kind}` });
    }
    return { rec: reconstructFile(ops), turnMap: map };
  }, [items]);
  if (!rec.rows.length && !rec.blind?.length) {
    return (
      <PayloadFrame
        strips={[{ label: "Nothing observed", tone: "warn", detail: "the trace records these ops but captured no content for them" }]}
        sources={payloadSources(items)}
       
      >
        <span className={`${META_CLASS} block px-[9px] py-[5px] text-[color:var(--muted)]`}>
          No content observed for this file in the trace.
        </span>
      </PayloadFrame>
    );
  }
  return (
    <PayloadFrame strips={fileBasis(rec, items)} sources={payloadSources(items)}>
      {rec.rows.length ? (
        <AnnotatedFileBody
          rec={rec}
          traceOf={turnMap}
          onSelectTrace={(t) => { if (typeof t === "number" && requestTurn) requestTurn(t); }}
          cap={36}
        />
      ) : null}
      {rec.blind?.length ? <DiffRows blind={rec.blind} /> : null}
    </PayloadFrame>
  );
}

// A traceOf map for the XRayBody renderers, which resolve an op's origin
// through `traceOf.get(promptId)`. In the feed the "trace" IS the open one, so
// the key is the turn index and the label its position.
//
// Timestamp: individual ops inside a trace have no time of their own (the
// store keeps no per-span timing), so every op reports the REQUEST's timestamp
// — the one real instant we have, and an upper bound on when the response was
// observed. Passing nothing renders "–" rather than an epoch date.
function turnTraceOf(items, traceTimestamp) {
  const m = new Map();
  for (const it of items) {
    if (it.turnIndex != null) {
      m.set(it.turnIndex, { summary: `Turn ${it.turnIndex}`, timestamp: it.op?.ts ?? traceTimestamp ?? undefined });
    }
  }
  return m;
}

// Shape a feed op into the `run` the shared ResponseBlock expects: it reads
// promptId/ts for the "response observed" line and table/view/result for the
// rich-vs-raw decision.
//
// `ts` must be undefined rather than null when nothing is known: ResponseBlock
// falls back to `run.ts` when the traceOf lookup misses, and sampleWhen turns
// null into the EPOCH (`new Date(null)`) while undefined renders "–".
function asRun(item, traceTimestamp) {
  return { ...item.op, promptId: item.turnIndex, ts: item.op?.ts ?? traceTimestamp ?? undefined };
}

// A doc replayed through the annotated rail — the same gutter treatment the
// scope X-rays use for observed payloads. `doc` is a traceFileTree op LIST;
// the per-op builders (opObservedDoc / logObsDoc) each return a single op, so
// they're normalized here rather than at every call site.
function AnnotatedDoc({ doc, items, cap = 24 }) {
  const traceTimestamp = useContext(TraceTimeContext);
  const requestTurn = useContext(TurnPreviewContext)?.request;

  const rec = useMemo(() => reconstructFile(Array.isArray(doc) ? doc : [doc]), [doc]);
  const traceOf = useMemo(() => turnTraceOf(items, traceTimestamp), [items, traceTimestamp]);
  if (!rec.rows.length) return null;
  return (
    <AnnotatedFileBody
      rec={rec}
      traceOf={traceOf}
      onSelectTrace={(t) => { if (typeof t === "number" && requestTurn) requestTurn(t); }}
      cap={cap}
    />
  );
}

// A single returned payload in a frame: one basis clause plus the turns it came
// from. The wrapper exists so each branch below declares its own basis in one
// line instead of nine near-identical frames.
function Observed({ detail, items, children }) {
  return (
    <PayloadFrame strips={observedBasis(detail)} sources={payloadSources(items)}>
      {children}
    </PayloadFrame>
  );
}

// A forge or Linear op returns SEVERAL documents at once — a PR body, the
// comments on it, the diff it carries — and each is its own small annotated
// document, exactly as the X-ray's target/issue bodies render them. One frame
// holds the set: a lone document leaves its name on the basis strip (nothing
// to disambiguate), several get captioned in place.
const presentDocs = (sections) => sections.filter((s) => s && (Array.isArray(s.doc) ? s.doc.length : s.doc));

function ObservedDocs({ sections, items }) {
  const present = presentDocs(sections);
  if (!present.length) return null;
  if (present.length === 1) {
    const only = present[0];
    return (
      <Observed detail={only.label} items={items}>
        <AnnotatedDoc doc={only.doc} items={items} cap={only.cap} />
      </Observed>
    );
  }
  return (
    <Observed detail="as returned to the agent" items={items}>
      <div className="grid min-w-0 gap-[6px] py-[3px]">
        {present.map((s, i) => (
          <div key={i} className="grid min-w-0 gap-[2px]">
            <span className={`${META_CLASS} px-[9px] text-[color:var(--muted)]`}>{s.label}</span>
            <AnnotatedDoc doc={s.doc} items={items} cap={s.cap} />
          </div>
        ))}
      </div>
    </Observed>
  );
}

// The expandable body of one member, or null when the summary line tells
// the whole story.
// Exported for the Lanes dock: both trace tabs read the same feed items, so
// they render the same payloads (the AnnotatedFileBody-from-XRayBody
// precedent, pointed the other way). `requestTurn` is optional — a host
// without inline turn previews gets static source chips, not dead buttons.
export function memberPayload(items, requestTurn, traceTimestamp) {
  const item = items[0];
  const op = item.op || {};
  switch (item.domain) {
    case "files":
      return <FilePayload items={items} />;
    case "research":
      // A fetched page has no writes — pageDoc maps it onto the read-observation
      // shape so the reconstruction rail renders what the agent actually saw
      // (and marks drift when two fetches of one URL disagree).
      if (op.kind === "fetch" && op.content) {
        return (
          <Observed detail="page as returned to the agent" items={items}>
            <AnnotatedDoc doc={pageDoc(items.map((it) => ({ ...it.op, promptId: it.turnIndex })))} items={items} cap={28} />
          </Observed>
        );
      }
      if (op.kind === "search" && op.results?.length) {
        return (
          <Observed detail="result list as returned" items={items}>
            <div className="grid min-w-0 gap-[2px] px-[9px] py-[4px]">
              {op.results.slice(0, 8).map((r) => (
                <span key={r.url} className={`${META_CLASS} truncate text-[color:var(--muted)]`}>
                  {r.title ? `${r.title}: ` : ""}
                  <span className="text-[color:var(--ink)]">{r.url}</span>
                </span>
              ))}
            </div>
          </Observed>
        );
      }
      return null;
    case "forge": {
      // Everything the shell op SHOWED the agent, through the same document
      // builders the X-ray's forge body uses — a PR body, the comments and
      // reviews on it, an observed diff, a CI log tail, a jq-shaped read.
      // Reading one of those only in the X-ray tab was a gap in this renderer,
      // not a difference between the two views.
      // extractForgeOps sets no promptId (the window pass adds it), so stamp
      // the turn index like the research and Linear branches do — the rail's
      // "observed at" label and its jump-to-turn both resolve through it.
      const ops = items.map((it) => ({ ...it.op, promptId: it.turnIndex }));
      const sections = [
        { label: "description as returned", doc: targetDescDoc(ops), cap: 26 },
        ...targetCommentDocs(ops).map((c) => ({
          label: [
            c.written ? "comment posted" : c.state ? `review observed · ${c.state.toLowerCase().replace(/_/g, " ")}` : "comment observed",
            c.author,
          ].filter(Boolean).join(" · "),
          doc: c.op,
          cap: 26,
        })),
        ...targetDiffDocs(ops).map((d) => ({
          label: `diff observed · ${d.lines}${d.truncated ? "+" : ""} line${d.lines === 1 ? "" : "s"}`,
          doc: d.op,
          cap: 26,
        })),
        ...ops.map(logObsDoc).filter(Boolean).map((doc) => ({ label: "CI log tail as returned", doc, cap: 26 })),
        ...ops.map(opObservedDoc).filter(Boolean).map((doc) => ({ label: "command output as returned", doc, cap: 26 })),
      ];
      if (presentDocs(sections).length) return <ObservedDocs sections={sections} items={items} />;
      // Text no builder models — still the output the agent read.
      const text = op.content || op.observedText || op.observedBody || null;
      return text ? (
        <Observed detail={op.log ? "CI log tail as returned" : "command output as returned"} items={items}>
          <div className="px-[9px] py-[4px]"><MonoBlock text={text} /></div>
        </Observed>
      ) : null;
    }
    case "linear": {
      // A description read and then written in the same trace replays as
      // write-over-read on the rail, so the edit shows as an edit. Comments
      // are their own one-version documents beside it — a list_comments read
      // carries its bodies on the op, so prose-only rendering dropped them
      // entirely here while the X-ray tab showed every one.
      const ops = items.map((it) => ({ ...it.op, promptId: it.turnIndex }));
      const sections = [
        { label: "issue description as returned", doc: issueDescDoc(ops), cap: 24 },
        ...issueCommentDocs(ops).map((c) => ({
          label: [c.written ? "comment written" : "comment observed", c.author].filter(Boolean).join(" · "),
          doc: c.op,
          cap: 24,
        })),
      ];
      if (presentDocs(sections).length) return <ObservedDocs sections={sections} items={items} />;
      return op.content ? (
        <Observed detail="comment body as returned" items={items}>
          <div className="px-[9px] py-[4px]"><MonoBlock text={op.content} /></div>
        </Observed>
      ) : null;
    }
    case "grafana":
    case "query": {
      const hasResponse = Boolean(op.result?.text || op.table || op.view);
      if (!op.query && !op.content && !hasResponse) return null;
      return (
        <PayloadFrame
          strips={observedBasis("query as authored, response as returned")}
          sources={payloadSources(items)}
         
        >
          <div className="grid min-w-0 gap-[4px] px-[9px] py-[4px]">
            {/* The query as authored — mono, unwrapped from its JSON envelope. */}
            {op.query ? <MonoBlock text={op.query} maxLines={12} /> : null}
            {op.content ? <MonoBlock text={op.content} maxLines={10} /> : null}
            {/* The response through the scope X-ray's own renderer: a result
                table (with CSV) or Grafana series/logs, Raw one click away. */}
            {hasResponse ? (
              <ResponseBlock
                run={asRun(item, traceTimestamp)}
                engine={op.engine}
                traceOf={turnTraceOf(items, traceTimestamp)}
                onSelectTrace={(t) => { if (typeof t === "number" && requestTurn) requestTurn(t); }}
                resCap={14}
              />
            ) : null}
          </div>
        </PayloadFrame>
      );
    }
    case "recall": {
      // The full consultation as the Recall X-ray shows it: the agent's own
      // articulation of the task, then the recalled world (topics,
      // neighbourhood, candidates by author). `bare` because the panel owns
      // the only scroll surface here.
      if (op.kind === "search") {
        return (
          <Observed detail="the recalled world, as the Recall X-ray reads it" items={items}>
            <RecallConsultationDetail
              bare
              consultation={{ ...op, promptId: item.turnIndex, ts: op.ts ?? traceTimestamp ?? undefined, endpointId: null, endpointName: null }}
              labelOf={(x) => x?.label || ""}
              onSelectTrace={(t) => { if (typeof t === "number" && requestTurn) requestTurn(t); }}
            />
          </Observed>
        );
      }
      return op.result?.text ? (
        <Observed detail="response as returned" items={items}>
          <div className="px-[9px] py-[4px]"><MonoBlock text={op.result.text} maxLines={14} /></div>
        </Observed>
      ) : null;
    }
    case "skill":
      return op.args && op.args.length > 80 ? (
        <Observed detail="invocation arguments as issued" items={items}>
          <div className="px-[9px] py-[4px]"><MonoBlock text={op.args} maxLines={8} /></div>
        </Observed>
      ) : null;
    case "artifact": {
      const lines = [op.description, op.path, op.url].filter(Boolean);
      return lines.length ? (
        <Observed detail="publish as recorded" items={items}>
          <div className="grid min-w-0 gap-[2px] px-[9px] py-[4px]">
            {lines.map((l, i) => (
              <span key={i} className={`${META_CLASS} break-all text-[color:var(--muted)]`}>{l}</span>
            ))}
          </div>
        </Observed>
      ) : null;
    }
    case "code": {
      // A cell no domain claimed: what it DID (the inner command, when one
      // was readable), the program that did it, and the host-envelope-stripped
      // output. Polls and stdin sends are plumbing — their summary line is
      // the whole story.
      if (op.kind !== "cell" || !(op.code || op.command)) return null;
      return (
        <Observed detail="cell as issued, output as returned" items={items}>
          <div className="grid min-w-0 gap-[4px] px-[9px] py-[4px]">
            {op.command ? <MonoBlock text={op.command} maxLines={12} /> : null}
            {op.code ? <MonoBlock text={op.codeTruncated ? `${op.code}…` : op.code} maxLines={op.command ? 8 : 16} /> : null}
            {op.output ? <MonoBlock text={op.outputTruncated ? `${op.output}…` : op.output} maxLines={10} /> : null}
          </div>
        </Observed>
      );
    }
    default: {
      const body = manifestRowBody(op);
      if (!body) return null;
      return (
        <Observed detail="as the lens read it" items={items}>
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] py-[2px]">
            {body.steps.map((x, i) => <KvRow key={`s${i}`} label={x.label}>{x.text}</KvRow>)}
            {body.sections.map((x, i) => <KvRow key={`x${i}`} label={x.label}>{x.text}</KvRow>)}
            {body.checks.map((x, i) => <KvRow key={`c${i}`} label={x.label} right={x.outcome}>{x.text}</KvRow>)}
          </div>
        </Observed>
      );
    }
  }
}

// A row's turn span, and the feed's primary locator into the Context reading.
// Right-aligned so every chip in the feed forms one column down the edge; mono
// and tabular so the numbers line up in it. It was 10px `--muted-soft` with no
// hover state, which read as a timestamp rather than a control.
function TurnChip({ span }) {
  const preview = useContext(TurnPreviewContext);
  if (!span) return null;
  const label = span.start === span.end ? `T${span.start}` : `T${span.start}–${span.end}`;
  const base = "shrink-0 font-mono text-[11px] tabular-nums leading-[1.2]";
  if (!preview) {
    return <span className={`${base} text-[color:var(--muted-soft)]`}>{label}</span>;
  }
  const active = preview.open === span.start;
  return (
    <button
      type="button"
      onClick={() => preview.request(span.start)}
      className={[
        base,
        "cursor-pointer border-b transition-colors",
        active
          ? "border-[color:var(--line-strong)] text-[color:var(--ink)]"
          : "border-transparent text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

// The one header-line shell every row variant shares: fixed 10px chevron
// slot, truncating title, optional meta, right-aligned turn chip. Keeping
// the geometry in one place is what holds the feed to a single left edge.
// The whole line is the hit target when it can open, with a hover fill —
// a 9px chevron alone was not a discoverable affordance.
function RowLine({ chevron = null, onToggle = null, title, titleClass = "text-[color:var(--ink)]", meta = null, right = null }) {
  const head = (
    <>
      <span className="w-[10px] shrink-0 text-[10px] leading-[1.45] text-[color:var(--muted-soft)]">{chevron}</span>
      <span className={`min-w-0 flex-1 truncate ${TITLE_CLASS} ${titleClass}`}>{title}</span>
    </>
  );
  return (
    <div
      className={[
        "flex min-w-0 items-baseline gap-[6px] rounded-[2px]",
        // No negative margin: bleeding the hover fill 6px past the content
        // column widened the panel's scrollWidth at narrow widths.
        onToggle ? "hover:bg-[color:var(--control-bg)]" : "",
      ].join(" ")}
    >
      {onToggle ? (
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 cursor-pointer items-baseline gap-[6px] text-left">
          {head}
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-baseline gap-[6px]">{head}</span>
      )}
      {meta ? (
        <span className={`${META_CLASS} max-w-[36%] shrink-0 truncate border-r border-[color:var(--line-soft)] pr-[9px] text-[color:var(--muted)]`}>
          {meta}
        </span>
      ) : null}
      {right}
    </div>
  );
}

// One member line inside a run: a file (with its whole op stack) or a
// single op, plus its payload toggle when there's something to show.
//
// `nested` says whether this line already sits inside a group's indent. It
// decides which of the two — line or payload — spends the feed's single indent
// step, so the payload lands at the same x either way (see INDENT).
function MemberLine({ member, nested = false }) {
  const [open, setOpen] = useState(false);
  const traceTimestamp = useContext(TraceTimeContext);
  const requestTurn = useContext(TurnPreviewContext)?.request;

  const items = member.items;
  const item = items[0];
  let title;
  let meta;
  if (item.domain === "files") {
    title = tailPath(item.op.path);
    meta = fileDigest(items);
  } else {
    ({ title, meta } = summarize(item));
    if (items.length > 1) title = `${title} ×${items.length}`;
  }
  const body = memberPayload(items, requestTurn, traceTimestamp);
  const failed = items.some((it) => it.op && it.op.ok === false);
  return (
    <div className="min-w-0 py-[2px]">
      <RowLine
        chevron={body ? (open ? "▾" : "▸") : null}
        onToggle={body ? () => setOpen((v) => !v) : null}
        title={title}
        meta={failed ? "failed" : meta}
        right={<TurnChip span={memberSpan(items)} />}
      />
      {open && body ? <div className={`${nested ? "" : INDENT} min-w-0 pt-[2px]`}>{body}</div> : null}
    </div>
  );
}

function OpsRow({ row }) {
  const members = useMemo(() => groupMembers(row.domain, row.items), [row.domain, row.items]);
  const single = members.length === 1;
  const [open, setOpen] = useState(false);
  const span = rowTurnSpan(row);

  // A run that groups to one member renders as that member directly — no
  // group shell around a single line. The domain still reads, because it lives
  // in the row's lane rather than in this title; when it was a prefix inside
  // the title, taking this shortcut silently dropped it, so a one-file run
  // showed a bare path and a two-file run showed "FILES".
  if (single) return <div className="py-[1px]"><MemberLine member={members[0]} /></div>;

  return (
    <div className="min-w-0 py-[3px]">
      <RowLine
        chevron={open ? "▾" : "▸"}
        onToggle={() => setOpen((v) => !v)}
        title={digest(row.domain, row.items)}
        right={<TurnChip span={span} />}
      />
      {open ? (
        <div className={`${INDENT} min-w-0 pt-[2px]`}>
          {members.map((m) => (
            <MemberLine key={m.items[0].id} member={m} nested />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// One unclaimed call, recovered. The residual is the feed's honest score — no
// lens matched, so nothing here is interpreted — but "no interpretation" was
// being rendered as "no content", which left the least-understood stretch of a
// trace as the only one a reader could not open at all.
function ResidualMember({ item }) {
  const [open, setOpen] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);
  const turns = useContext(TraceTurnsContext);
  // The head reads on mount for the row's preview line; the full pair (with its
  // result scan) only when the row is actually opened.
  const head = useMemo(
    () => (turns && item.turnIndex != null ? readToolCallHead(turns, item.turnIndex, item.blockIndex) : null),
    [turns, item.turnIndex, item.blockIndex],
  );
  const call = useMemo(
    () => (open && turns && item.turnIndex != null ? readToolCall(turns, item.turnIndex, item.blockIndex) : null),
    [open, turns, item.turnIndex, item.blockIndex],
  );
  const span = item.turnIndex != null ? { start: item.turnIndex, end: item.turnIndex } : null;
  return (
    <div className="min-w-0 py-[2px]">
      <RowLine
        chevron={head ? (open ? "▾" : "▸") : null}
        onToggle={head ? () => setOpen((v) => !v) : null}
        // The tool's name is the least informative thing about a shell call, so
        // the row leads with what the call actually did. Without it a residual
        // run is a stack of identical "Bash" rows you have to open one by one to
        // find the one you're looking for.
        title={
          head?.firstLine ? (
            <>
              <span className="text-[color:var(--muted-soft)]">{head.name}</span>
              <span className="px-[6px] text-[color:var(--muted-soft)]">·</span>
              <span className="font-mono text-[11.5px] text-[color:var(--muted)]">{head.firstLine}</span>
            </>
          ) : (
            item.op.name
          )
        }
        titleClass="text-[color:var(--muted)]"
        meta={(call?.failed ?? head?.failed) ? "failed" : head?.moreLines ? `+${head.moreLines} args` : null}
        right={<TurnChip span={span} />}
      />
      {open && call ? (
        <div className="min-w-0 pt-[2px]">
          <PayloadFrame
            strips={[{ label: "Unclaimed", detail: "No lens matched this call. Showing captured data." }]}
            sources={call.sources}
          >
            {call.call ? (
              <KvRow label="Call" right={call.callClipped ? `+${call.callClipped} chars` : null}>{call.call}</KvRow>
            ) : null}
            {call.result ? (
              <KvRow
                label="Result"
                tone="soft"
                right={[call.resultNote, call.resultClipped ? `+${call.resultClipped} chars` : null].filter(Boolean).join(" · ") || null}
              >
                {call.result}
              </KvRow>
            ) : (
              <KvRow label="Result" tone="soft">No result was recorded for this call.</KvRow>
            )}
            {/* The line reading flattens structure; the raw input is the escape
                hatch for an argument that reading loses. */}
            {call.callJson && call.callJson !== call.call ? (
              <button
                type="button"
                onClick={() => setRawOpen((v) => !v)}
                className={`block w-full border-t border-[color:var(--line-soft)] px-[9px] py-[3px] text-left font-mono text-[11px] text-[color:var(--muted)] cursor-pointer hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)]`}
              >
                {rawOpen ? "⌃ hide raw input" : "⌄ raw input"}
              </button>
            ) : null}
            {rawOpen ? <KvRow label="Raw" tone="soft">{call.callJson}</KvRow> : null}
          </PayloadFrame>
        </div>
      ) : null}
    </div>
  );
}

// An unclassified stretch: the dotted spine segment, a hollow node, and a
// one-line name summary that opens into the calls themselves. It stays
// subordinate to real work through the spine and the muted title — not by
// being too small to read, which is what the previous ⋯ interstitial did at
// 9.5px. Hiding it outright would let the feed overstate what it classified.
function ResidualRow({ row, expandAllTick = 0 }) {
  const [open, setOpen] = useState(false);
  // The coverage line's "expand all" reaches every residual run at once: with a
  // dozen scattered runs, opening each by hand is the whole cost of checking
  // what the reading missed.
  useEffect(() => {
    if (expandAllTick > 0) setOpen(true);
  }, [expandAllTick]);
  const names = residualSummary(row.items);
  const summary = names.map(({ name, n }) => (n > 1 ? `${name} ×${n}` : name)).join(" · ");
  return (
    <div className="min-w-0 py-[2px]">
      <RowLine
        chevron={open ? "▾" : "▸"}
        onToggle={() => setOpen((v) => !v)}
        title={summary}
        titleClass="text-[color:var(--muted)]"
        meta="unclassified"
        right={<TurnChip span={rowTurnSpan(row)} />}
      />
      {open ? (
        <div className={`${INDENT} min-w-0 pt-[1px]`}>
          {row.items.map((item) => (
            <ResidualMember key={item.id} item={item} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// The timeline gutter cell: one continuous spine per feed, drawn per-row so
// each row variant can pick its segment style. Solid + node = a real event;
// dotted + no node = an unclassified stretch; ring = a harness notice.
//
// It holds nothing else, and nothing interactive. The ENDPOINT's activity used
// to thread down it as marks, one time-slice per row, with the affordance that
// opened a window buried in ONE row's 16px column — so most rows of a stretch
// carried marks indistinguishable from the one that could be opened and doing
// nothing at all. Both moved to the window's own boundary row: the one place the
// window is bounded, and the one place it can be opened.
function GutterCell({ kind }) {
  return (
    <div className="relative">
      <span aria-hidden="true" className="pointer-events-none">
        <span
          className={`absolute bottom-0 top-0 left-[6px] w-0 border-l ${
            kind === "residual" ? "border-dotted border-[color:var(--line-strong)]" : "border-[color:var(--line-strong)]"
          }`}
        />
        {/* "span" carries the spine through a preview without adding a node —
            the preview is a reading of a turn already marked, not a new event. */}
        {kind === "op" ? (
          <span className="absolute left-[4px] top-[9px] h-[5px] w-[5px] rounded-full bg-[color:var(--muted-soft)]" />
        ) : kind === "notice" ? (
          <span className="absolute left-[4px] top-[9px] h-[5px] w-[5px] rounded-full border border-[color:var(--muted-soft)] bg-[color:var(--bg)]" />
        ) : kind === "reply" ? (
          // Paired with the prompt's node (same color) but hollow and smaller:
          // the answering half of the episode, subordinate to the intent.
          <span className="absolute left-[3.5px] top-[10px] h-[7px] w-[7px] rounded-full border border-[color:var(--eyebrow)] bg-[color:var(--bg)]" />
        ) : kind === "prompt" ? (
          <span className="absolute left-[2.5px] top-[10px] h-[8px] w-[8px] rounded-full bg-[color:var(--eyebrow)]" />
        ) : null}
      </span>
    </div>
  );
}

// A capture instant: the tick across the rail, its clock reading, and whatever
// the endpoint was doing in the window that instant opens.
//
// Two moves, and each of them is the same move. The clock reading left the lane
// because printed there it was a column of labels BESIDE the timeline, repeated
// per row and suppressed wherever it did not advance — a value, restated. A rung
// boundary is not a value: it is the moment a request was captured, the ceiling
// on everything above and the floor under everything below. So it crosses the
// spine, once. But it is still a LABEL, so it prints at the lane's own left edge
// rather than floating in the gutter beside it: one column of labels down the
// page, with the times among the domain tags instead of alongside them.
//
// The endpoint's marks then sit beside it, in the content column, because the
// window they describe is the span this instant opens. That is also what makes
// this row the window's ONE control — see EndpointWindow, which renders into
// `children`.
//
// The spine is drawn on the outer element rather than inside the tick, so an
// opened window does not read as a break in the timeline.
function RailBoundary({ at, children = null }) {
  return (
    // data-rail-boundary marks the one element outside the feed's grid rows
    // that a reader can meet on the rail. TraceWorkFeed.test.jsx measures the
    // two invariants against it: the boundary's fixed columns are the grid's
    // own, and every event mark on the page sits inside one.
    <div data-rail-boundary="" className="relative min-w-0">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 top-0 left-[6px] w-0 border-l border-[color:var(--line-strong)]"
      />
      <div className="flex min-w-0 items-start py-[5px]">
        <span aria-hidden="true" className={`relative shrink-0 ${SPINE_COL} ${BOUNDARY_LINE}`}>
          <span className="absolute left-[3px] top-[7px] w-[7px] border-t border-[color:var(--line-strong)]" />
        </span>
        <div className={`shrink-0 ${LANE_COL} font-mono text-[10px] leading-[1.4] tabular-nums text-[color:var(--muted-soft)]`}>
          {at}
        </div>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}

// The episode header: the prompt beat as a filled box, sticky to the top of
// the panel scroll while its episode's ops go by. Clamped to two lines;
// click to unfold.
function PromptHeader({ item }) {
  const [expanded, setExpanded] = useState(false);
  const span = item.turnIndex != null ? { start: item.turnIndex, end: item.turnIndex } : null;
  const long = (item.op.text || "").length > 140 || (item.op.text || "").includes("\n");
  const body = (
    <div className={`whitespace-pre-wrap break-words ${TITLE_CLASS} text-[color:var(--ink)] ${long && !expanded ? "line-clamp-2" : ""}`}>
      {item.op.text}
      {item.op.truncated ? <span className="text-[color:var(--muted-soft)]"> …</span> : null}
    </div>
  );
  return (
    <div className={`grid min-w-0 ${GUTTER_COLS} py-[3px]`}>
      <GutterCell kind="prompt" />
      {/* Spans the lane: the full-width beat boxes are what give the feed its
          episode structure, and a prompt carries no domain to state in it. */}
      <div className="col-span-2 min-w-0 rounded-[2px] bg-[color:var(--control-bg)] px-[9px] py-[6px]">
        <div className="flex items-baseline gap-[6px] pb-[2px]">
          <span className={`${EYEBROW_CLASS} flex-1`}>Prompt</span>
          <TurnChip span={span} />
        </div>
        {long ? (
          <button type="button" onClick={() => setExpanded((v) => !v)} className="block w-full cursor-pointer text-left">
            {body}
          </button>
        ) : (
          body
        )}
      </div>
    </div>
  );
}

// The episode's outcome: what the agent said back when it stopped working.
// Deliberately subordinate to the prompt box — hairline instead of fill — so
// an episode reads intent (filled) → work (rows) → answer (outlined), and the
// prompt stays the dominant marker. Clamped to three lines; click to unfold.
function ReplyBeat({ item }) {
  const [expanded, setExpanded] = useState(false);
  const span = item.turnIndex != null ? { start: item.turnIndex, end: item.turnIndex } : null;
  const long = (item.op.text || "").length > 180 || (item.op.text || "").includes("\n");
  const body = (
    <div className={`whitespace-pre-wrap break-words ${TITLE_CLASS} text-[color:var(--ink)] ${long && !expanded ? "line-clamp-3" : ""}`}>
      {item.op.text}
      {item.op.truncated ? <span className="text-[color:var(--muted-soft)]"> …</span> : null}
    </div>
  );
  return (
    <div className="min-w-0 py-[3px]">
      <div className="min-w-0 rounded-[2px] border border-[color:var(--line-soft)] px-[9px] py-[5px]">
        <div className="flex items-baseline gap-[6px] pb-[2px]">
          <span className={`${EYEBROW_CLASS} flex-1`}>Reply</span>
          <TurnChip span={span} />
        </div>
        {long ? (
          <button type="button" onClick={() => setExpanded((v) => !v)} className="block w-full cursor-pointer text-left">
            {body}
          </button>
        ) : (
          body
        )}
      </div>
    </div>
  );
}

// A harness notification that arrived as a user turn: a minor beat, not an
// episode. The bracket banner ("[SYSTEM NOTIFICATION - NOT USER INPUT]") is
// boilerplate — show what follows it.
function stripNoticeBanner(text) {
  const s = String(text || "");
  const m = /^\[[^\]\n]*\]\s*/.exec(s);
  const rest = (m ? s.slice(m[0].length) : s).trim();
  return rest || s;
}

// The "Notice" label lives in the row's lane like any other domain, so this
// renders only the text — one row, one line, same as everything else.
function NoticeRow({ item }) {
  const [open, setOpen] = useState(false);
  const span = item.turnIndex != null ? { start: item.turnIndex, end: item.turnIndex } : null;
  const text = stripNoticeBanner(item.op.text);
  return (
    <div className="min-w-0 py-[2px]">
      <div className="flex min-w-0 items-baseline gap-[6px] rounded-[2px] hover:bg-[color:var(--control-bg)]">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 cursor-pointer items-baseline gap-[6px] text-left"
        >
          <span className={`min-w-0 flex-1 ${META_CLASS} text-[color:var(--muted)] ${open ? "whitespace-pre-wrap break-words" : "truncate"}`}>
            {text}
          </span>
        </button>
        <TurnChip span={span} />
      </div>
    </div>
  );
}

// What was happening on the machine while a stretch of the conversation ran,
// read in three tiers, quietest by default.
//
// The distinction the tiers encode: an ambient COUNT is not evidence. "401 file
// changes in this window" is true of nearly every window on a working machine
// and names nothing a reader can act on — rendered as a line under every row it
// is scaffolding for a fact nobody asked for. A name MATCH is different in kind:
// the endpoint wrote the very file this stretch edited, or started a process
// whose command line is the shell command it ran. That is worth interrupting the
// page for; the counts are worth a run of marks beside the boundary.
//
// Subordinate at every tier, and that is structural rather than typographic: all
// of it lives on a rail boundary the clock already draws, takes no node on the
// spine, adds no grid row, and renders NOTHING when the window held nothing. An
// absence has to cost zero pixels. The turn reading has to be complete without
// any of it.
//
// A window is a rung of the turn clock, so its ceiling is a REQUEST's capture
// time rather than a turn's — an upper bound. Hence "during this window" and
// never "this turn ran this": no id joins the two (see
// lib/traceEndpointCorroboration.js). A name match narrows the coincidence; it
// does not turn it into a cause, and the copy at every tier says so.
//
// TIER 1 is the run of marks — drawn, never written, which is the whole reason a
// count can be shown for every window without any of them reading as a claim.

// One correspondence, as it reads on the page. Kept here rather than in the
// matcher because the matcher owns what agreed, not how it is phrased.
function matchLine(m) {
  if (m.kind === "ProcessStart") return `${m.verb} ${clip(m.observed, 90)}`;
  return `${tailPath(m.observed)} ${m.verb}`;
}

// A lookup that did not answer, as a mark rather than a sentence — the sentence
// was the clutter. Never "this session's processes did nothing": that is a claim
// about the session, and a lookup that never completed made no such claim.
//
// TWO refusals, and telling them apart is the whole job of the mark. UNREAD is
// a limit of the reading: the session produced more events across the trace's
// span than one read carries, so the reading stopped before reaching this
// window. FAILED is a fault: the question went out and did not come back.
//
// Phrased as "not read for this window" the first reads as a fact about the
// boundary it sits on — which on a trace where dozens of boundaries carry the
// identical mark is the one thing that is certainly untrue. So the copy names
// the BOUND instead of the window, and the feed header carries how many windows
// are in the same state (see the coverage score in TraceWorkFeed).
//
// Deliberately NOT an event mark and deliberately not a control: there is
// nothing behind it to open, and a reader who found one that did not respond
// would have to learn which marks are live and which are not.
function CorroborationUnavailable({ reason }) {
  const label =
    reason === "truncated"
      ? `Session activity is unavailable here. This window falls outside the first ${SPAN_EVENT_LIMIT.toLocaleString()} events in the trace.`
      : "Could not read session activity for this window. The trace lookup failed.";
  return (
    <HoverTooltip label={label} placement="top">
      <span
        role="img"
        aria-label={label}
        className={`flex items-center text-[9px] leading-none text-[color:var(--muted-soft)] ${BOUNDARY_LINE}`}
      >
        {reason === "truncated" ? "⋯" : "⚠"}
      </span>
    </HoverTooltip>
  );
}

// The window's whole reading, on the boundary that opens it: the run of marks,
// the correspondences worth a line, and the expansion behind both.
//
// ONE control per window, and every mark inside it. That is the fix for the
// reading this replaced, where the marks were spread down a stretch and only the
// stretch's first row could be opened — so a reader met identical marks on four
// rows, three of which did nothing, and had no way to tell which was which.
// Because the marks live in the control, "can I open this" has a single answer
// everywhere: yes.
//
// The whole content column is the hit target rather than the marks themselves. A
// 2–4px square is a mark, not a button; the row it sits on is the button.
function EndpointWindow({ window: win, candidates, open, onToggle }) {
  const corroboration = useContext(EndpointCorroborationContext);
  const request = corroboration?.request;
  // Two things want the detail projection and they arrive at different times.
  // MATCHING wants it wherever the rows named a path or ran a command, decided
  // here from the rows because that is stable — a window that names nothing
  // today names nothing later. READING wants it wherever someone opened the
  // window, decided by the click, because "what actually ran" is a question
  // about events and not about correspondence. Either is enough to ask; the
  // hook's legs are independent, so the second ask costs only the leg it needs.
  const wantDetail = hasCandidates(candidates) || open;
  useEffect(() => {
    // `request` is idempotent per window key AND per leg, so a re-ask costs a
    // comparison and an upgrade costs exactly the detail query. `open` rides
    // along as the interactive flag: MATCHING is speculative and shares the
    // detail budget, READING was asked for and does not.
    if (request) request(win, wantDetail, open);
  }, [request, win, wantDetail, open]);

  const entry = corroboration?.resultFor(win.key) || null;
  const matches = useMemo(
    () => (entry?.status === "ready" && entry.detail?.events ? matchWindowEvents(entry.detail.events, candidates) : []),
    [entry, candidates],
  );
  // Only folded for a window someone opened: it is a read of up to DETAIL_LIMIT
  // events and every dated window in the feed holds one.
  const events = useMemo(
    () => (open && entry?.detail?.events ? windowEventRows(entry.detail.events, matches) : null),
    [open, entry, matches],
  );

  // Un-asked or still out: the header carries the progress, so a boundary that
  // does not yet know spends nothing saying so.
  if (!entry) return null;
  if (entry.status === "unavailable") return <CorroborationUnavailable reason={entry.reason} />;
  if (entry.status !== "ready") return null;
  const groups = corroborationGroups(entry.events);
  // Nothing happened in this window. The boundary keeps its time and costs the
  // page nothing else — an absence has to be free.
  if (!groups.length) return null;

  const marks = windowMarks(entry.events);
  const digest = corroborationDigest(entry.events);
  const shown = matches.slice(0, MATCH_LINE_CAP);
  const extra = matches.length - shown.length;
  // Asked for and still out, as against asked for and refused.
  // An open window's names are always read — the budget bounds prefetch, not
  // interaction — so a refusal one still carries is a frame stale: the retry
  // goes out in the effect above. Reading it as "working" rather than "not
  // read" is what keeps the refusal from flashing on every such open.
  const readingNames = open && (entry.detail == null || entry.detail.budget === true);
  const span = [clockLabel(win.startTs), clockLabel(win.endTs)].filter(Boolean).join("–");
  // No budget wording here: this block only renders for an open window, and an
  // open window's names are never refused, so a reader can no longer be told
  // about a bound they cannot hit. The refusal still exists in the hook — it is
  // what lets an open tell "the prefetch skipped this" apart from "nobody ever
  // wanted names here" and retry only the first.
  const namesRead = entry.detail?.failed
    ? " · Could not read names"
    : entry.detail?.capped
      ? `, names read for the first ${entry.detail.events.length} by time`
      : "";

  return (
    <div className="min-w-0">
      {/* No text at all: the reading in words is the accessible name and the
          hover label. A boundary that spelled out "4 processes · 2 file changes"
          on every rung would turn the rail into a second column of prose and
          make the boundary read as a beat of the conversation.
          Full width and flush left, like RowLine's own hit target: the run
          starts exactly where a row's chevron starts, which is the column the
          feed already means "this opens". */}
      <HoverTooltip label={`Session processes on this endpoint during this window: ${digest}`} placement="top">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`session activity on the endpoint: ${digest}`}
          className={[
            `flex w-full min-w-0 cursor-pointer items-center gap-[4px] rounded-[2px] transition-colors ${BOUNDARY_LINE}`,
            open ? "bg-[color:var(--control-bg)]" : "hover:bg-[color:var(--control-bg)]",
          ].join(" ")}
        >
          {marks.map((m) => {
            const side = MARK_SIDE[m.weight] || MARK_SIDE[1];
            return (
              <span
                key={m.kind}
                data-event-mark={m.kind}
                aria-hidden="true"
                className="shrink-0"
                style={{
                  height: side,
                  width: side,
                  backgroundColor: `color-mix(in srgb, var(${open ? "--ink" : "--muted-soft"}) ${MARK_ALPHA[m.weight] || MARK_ALPHA[1]}%, transparent)`,
                }}
              />
            );
          })}
        </button>
      </HoverTooltip>
      {shown.map((m, i) => (
        <button
          key={m.key}
          type="button"
          onClick={onToggle}
          className="flex min-w-0 max-w-full cursor-pointer items-baseline gap-[5px] rounded-[2px] pt-[1px] text-left text-[10px] leading-[1.4] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)]"
        >
          <span aria-hidden="true" className="shrink-0">{kindGlyph(m.kind)}</span>
          <span className="min-w-0 truncate">{matchLine(m)}</span>
          <span className="shrink-0 tabular-nums text-[color:var(--muted-soft)]">{eventClockLabel(m.ts) || "–"}</span>
          {/* The register, on every line that shows a match: one of this
              session's processes touched this name inside this window. It did not
              do it because of the turn, and nothing available here could
              establish that it had. */}
          <span className="shrink-0 text-[color:var(--muted-soft)]">
            {i === shown.length - 1 && extra > 0 ? `name match; no attribution · +${extra} more` : "name match; no attribution"}
          </span>
        </button>
      ))}
      {open ? (
        // The feed's single indent step (see INDENT). This is not a payload
        // frame and carries no data-payload-frame marker: the strict indent
        // invariant is measured against those, and an annotation is not one.
        <div className={`${INDENT} min-w-0 pt-[2px]`}>
          <div className="min-w-0 rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)]">
            <div className={`flex min-w-0 flex-wrap items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[4px] ${EYEBROW_CLASS}`}>
              <span className="shrink-0 whitespace-nowrap">Session activity</span>
              <span className={`${META_CLASS} min-w-0 flex-1 normal-case tracking-normal text-[color:var(--muted)]`}>
                {`${span} · this session's processes on the endpoint${entry.capped ? `, first ${entry.events.length} by time` : ""}${namesRead}`}
              </span>
            </div>
            {matches.length ? (
              <>
                <div className={`border-b border-[color:var(--line-soft)] px-[9px] py-[3px] ${EYEBROW_CLASS}`}>Name matches</div>
                {matches.map((m) => (
                  <div key={m.key} className="flex min-w-0 items-baseline gap-[9px] px-[9px] py-[2px]">
                    <span aria-hidden="true" className={`${META_CLASS} shrink-0 text-[color:var(--muted-soft)]`}>{kindGlyph(m.kind)}</span>
                    <span className={`${META_CLASS} min-w-0 flex-1 truncate text-[color:var(--ink)]`}>{matchLine(m)}</span>
                    {/* What agreed — the difference between "the same path" and
                        "the file and the directory holding it" is the difference
                        between evidence and a shared filename. */}
                    <span className={`${META_CLASS} shrink-0 text-[color:var(--muted)]`}>{m.basis}</span>
                    <span className={`${META_CLASS} shrink-0 tabular-nums text-[color:var(--muted-soft)]`}>
                      {m.count > 1 ? `×${m.count} · ` : ""}
                      {eventClockLabel(m.ts) || "–"}
                    </span>
                  </div>
                ))}
              </>
            ) : null}
            {/* The events themselves, which is what someone opening a window
                came for. The counts below are a tally of what happened; these
                are what happened. */}
            {events?.rows.length ? (
              <>
                <div className={`flex min-w-0 flex-wrap items-baseline gap-[8px] border-y border-[color:var(--line-soft)] px-[9px] py-[3px] ${EYEBROW_CLASS}`}>
                  <span className="shrink-0 whitespace-nowrap">Process and file events</span>
                  {/* The ordering is stated because it is a claim: it says the
                      rows below are worth reading in this order, and a reader
                      who does not know the rule cannot tell whether a row is
                      near the top because it matters or because it is early. */}
                  <span className={`${META_CLASS} min-w-0 flex-1 normal-case tracking-normal text-[color:var(--muted)]`}>
                    {[
                      events.hidden > 0
                        ? `${events.rows.length} of ${events.distinct} distinct, folded from ${events.read}`
                        : `${events.distinct} distinct, folded from ${events.read}`,
                      matches.length ? "name matches first, then least frequent" : "least frequent first",
                      events.hidden > 0 ? `${events.hidden} most frequent hidden` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                {events.rows.map((r) => (
                  <div key={r.key} className="flex min-w-0 items-baseline gap-[9px] px-[9px] py-[2px]">
                    <span aria-hidden="true" className={`${META_CLASS} shrink-0 text-[color:var(--muted-soft)]`}>{kindGlyph(r.kind)}</span>
                    {/* Wraps to two lines rather than truncating: the tail of a
                        command line is routinely the part that says what it was
                        for, and a path's last segments are the whole reading. */}
                    <span
                      className={`min-w-0 flex-1 break-all font-mono text-[11px] leading-[1.4] line-clamp-2 ${r.matched ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}
                    >
                      {r.text}
                    </span>
                    <span className={`${META_CLASS} shrink-0 text-[color:var(--muted-soft)]`}>{r.verb}</span>
                    <span className={`${META_CLASS} shrink-0 tabular-nums text-[color:var(--muted)]`}>
                      {r.count > 1 ? `×${r.count}` : ""}
                    </span>
                    <span className={`${META_CLASS} shrink-0 tabular-nums text-[color:var(--muted-soft)]`}>
                      {eventClockLabel(r.firstTs) || "–"}
                    </span>
                  </div>
                ))}
              </>
            ) : readingNames ? (
              // The detail leg is out. aria-busy is the detectable marker (see
              // dashboard/AGENTS.md); without it "still reading" and "nothing
              // to read" are the same DOM.
              <div aria-busy="true" className={`border-y border-[color:var(--line-soft)] px-[9px] py-[3px] ${META_CLASS} text-[color:var(--muted-soft)]`}>
                Reading activity…
              </div>
            ) : null}
            {/* Named only when there is a section above it to be told apart
                from — on its own, a header over the only list in the frame says
                nothing the frame's own title has not. */}
            {matches.length || events?.rows.length || readingNames ? (
              <div className={`border-b border-[color:var(--line-soft)] px-[9px] py-[3px] ${EYEBROW_CLASS}`}>
                All activity
              </div>
            ) : null}
            {groups.map((g) => (
              <div key={g.kind} className="flex min-w-0 items-baseline gap-[9px] px-[9px] py-[2px]">
                <span aria-hidden="true" className={`${META_CLASS} shrink-0 text-[color:var(--muted-soft)]`}>{kindGlyph(g.kind)}</span>
                <span className={`${META_CLASS} min-w-0 flex-1 truncate text-[color:var(--ink)]`}>{g.label}</span>
                <span className={`${MONO_CLASS} shrink-0 tabular-nums text-[color:var(--muted)]`}>{g.count}</span>
                <span className={`${META_CLASS} shrink-0 tabular-nums text-[color:var(--muted-soft)]`}>
                  {g.firstTs == null
                    ? "–"
                    : g.firstTs === g.lastTs
                      ? clockLabel(g.firstTs)
                      : `${clockLabel(g.firstTs)}–${clockLabel(g.lastTs)}`}
                </span>
              </div>
            ))}
            <div className={`border-t border-[color:var(--line-soft)] px-[9px] py-[4px] ${META_CLASS} text-[color:var(--muted-soft)]`}>
              {"The endpoint attributes these events to session processes. Time is the only link to this window; turn attribution is unavailable."}
              {matches.length
                ? " A name match means a session process touched the same name during this window. Turn-level causation is unknown."
                : ""}
              {/* The list and the counts cover different sets, and the reader
                  has no way to see that from the rows: only the kinds that can
                  carry a name are read back with their payload. Stated whenever
                  the names WERE read, including when that turned up no list at
                  all — a window of pure network activity is exactly the case
                  where an unexplained empty list reads as a bug. */}
              {entry.detail?.events
                ? " Individual rows include only process and file events. Counts include every event type."
                : ""}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Rows partitioned into episodes: each USER prompt beat starts a section and
// owns every row until the next one, so its sticky header releases exactly
// when the next episode arrives. Notices stay inside the current episode.
// `index` is the row's position in feed.rows, preserved for focus keys.
function partitionEpisodes(rows) {
  const episodes = [];
  let current = { prompt: null, promptIndex: -1, rows: [] };
  rows.forEach((row, index) => {
    if (row.type === "prompt" && row.item.op.kind !== "notice") {
      if (current.prompt || current.rows.length) episodes.push(current);
      current = { prompt: row, promptIndex: index, rows: [] };
    } else {
      current.rows.push({ row, index });
    }
  });
  if (current.prompt || current.rows.length) episodes.push(current);
  return episodes;
}

// A prompt or reply is the message occurrence that closes its preceding interval.
// The clock model deliberately keeps the whole turn in that interval for
// endpoint correlation; the renderer alone moves the interval's closing tick
// ahead of the message beat so the visual chronology agrees with message_at.
function messageBoundaryLayout(rows, boundaries) {
  const movedBefore = new Map();
  const consumedBefore = new Set();
  let consumedClose = false;
  rows.forEach((row, index) => {
    if (row.type !== "prompt" && row.type !== "reply") return;
    const following = boundaries.before.get(index + 1);
    if (following) {
      movedBefore.set(index, { at: following, sourceIndex: index + 1 });
      consumedBefore.add(index + 1);
    } else if (boundaries.closeIndex === index && Number.isFinite(boundaries.closeTs)) {
      movedBefore.set(index, { at: { ts: boundaries.closeTs, window: null }, sourceIndex: null });
      consumedClose = true;
    }
  });
  return { movedBefore, consumedBefore, consumedClose };
}

// The lane cell: a row's domain, in a fixed column. Rendering it for EVERY op
// row — grouped or not — is what gives the feed a scannable title column and
// what stops the domain from silently disappearing on a one-member run.
function LaneCell({ label = null, dim = false }) {
  // Always renders, even empty: returning null let the following child slide
  // into the lane column, which is what crushed and then shifted the beat boxes.
  return (
    <div className="min-w-0 pt-[6px]">
      {label ? (
        <div className={`${EYEBROW_BASE} truncate ${dim ? "text-[color:var(--muted-soft)]" : "text-[color:var(--eyebrow)]"}`}>
          {label}
        </div>
      ) : null}
    </div>
  );
}

// A row's lane label. Residual runs and harness notices are typed too — they
// are just not lens-claimed work, which the spine and the muted title say.
function laneLabelOf(row, domains) {
  if (row.type === "residual") return "Tool";
  if (row.type === "prompt") return "Notice";
  if (row.type === "reply") return null;
  return domains[row.domain]?.label || row.domain;
}

// turnFocus: the turn the reader deliberately selected in another reading (see
// lib/traceTurnFocus.js). A turn can span several op rows here, so the feed
// highlights every row whose span CONTAINS it and scrolls to the first.
// onJumpToTurn is the pivot BACK, and it is now only ever reached by an explicit
// "open in Context" inside a turn preview. Nothing else in the feed navigates:
// every evidence affordance resolves against the trace in place.
export default function TraceWorkFeed({ turns, onJumpToTurn = null, turnFocus = null, turnFocusArrival = 0, traceTimestamp = undefined, sessionId = null, traceId = null, endpointId = null }) {
  // Behaviour artifacts execute OFF the render path (isolated realm, cached
  // per trace × artifact version) and merge in when they land — the feed
  // rebuilds once with their ops claiming block ids through the same door as
  // every extractor, so coverage/residual stays one honest number. Until they
  // land (or when one throws), their work reads as residual, which is the
  // honest interim state rather than a loading lie.
  const [artifactGroups, setArtifactGroups] = useState([]);
  const [artifactTick, setArtifactTick] = useState(0);
  useEffect(() => subscribeBehaviourArtifacts(() => setArtifactTick((n) => n + 1)), []);
  useEffect(() => {
    // Identity-stable reset: replacing [] with a new [] would re-render and
    // re-fire every rows-keyed effect (the focus scroll, the turn observer)
    // for nothing.
    setArtifactGroups((prev) => (prev.length ? [] : prev));
    if (!listBehaviourArtifacts().length || !Array.isArray(turns) || !turns.length) return undefined;
    const ctrl = new AbortController();
    artifactOpsForTrace(
      { traceId, sessionId, endpointId, ts: traceTimestamp, turns },
      { signal: ctrl.signal },
    ).then(({ groups, problems }) => {
      if (ctrl.signal.aborted) return;
      for (const problem of problems) {
        console.warn(`[xray] behaviour artifact on this trace: ${problem}`);
      }
      if (groups.length) setArtifactGroups(groups);
    }).catch(() => { /* a failed run reads as residual, never as a crash */ });
    return () => ctrl.abort();
  }, [turns, traceId, sessionId, endpointId, traceTimestamp, artifactTick]);

  const feed = useMemo(
    () => buildSemanticFeed(turns, { extraOps: artifactGroups }),
    [turns, artifactGroups],
  );
  const episodes = useMemo(() => partitionEpisodes(feed.rows), [feed.rows]);
  const rowRefs = useRef(new Map());
  // The rows carrying the shared turn coordinate. Persistent, not a flash: it
  // marks where the reading you came from was pointing, so it has to survive
  // long enough to read the row. Same ring as the Context card.
  const [focusKeys, setFocusKeys] = useState(null);
  const focusRing = (key) => (focusKeys?.has(key)
    ? { background: "var(--green-soft)", boxShadow: "inset 2px 0 0 var(--green)" }
    : undefined);
  const [expandAllTick, setExpandAllTick] = useState(0);
  // One preview open at a time, keyed by the row that asked. Scoping it to the
  // feed rather than per-row is what keeps previews from accumulating down the
  // page as a reader checks one source after another.
  const [preview, setPreview] = useState(null);
  // One endpoint window expanded at a time, keyed by the window rather than the
  // row: a window has exactly one carrier row, and keying on it means the state
  // survives the carrier moving as more of the trace gets dated.
  const [openWindow, setOpenWindow] = useState(null);

  // Turn dating. The focal request's own turn count is free — its body is what
  // the feed is rendering — so the ladder starts with one rung already known.
  const clock = useTurnClock({
    turns,
    sessionId,
    focal: { eventId: traceId, ts: traceTimestamp, turnCount: Array.isArray(turns) ? turns.length : 0 },
    enabled: Boolean(sessionId) && Number.isFinite(traceTimestamp),
  });
  const { requestTurn } = clock;

  // Endpoint corroboration rides the clock's laziness rather than adding its
  // own: a window only exists once a row it covers has been dated, and only a
  // row that scrolled into view gets dated.
  // The span is the clock's, not this component's: every window it can produce
  // lies inside [first prior capture, focal capture], which is exactly what lets
  // the reading be fetched once for the whole trace instead of once per window.
  const corroboration = useEndpointCorroboration({
    sessionId,
    endpointId,
    span: clock.span,
    enabled: Boolean(sessionId) && Boolean(endpointId),
  });

  // Dating is driven by what is ON SCREEN, not by what is mounted: the feed
  // renders every row, so asking on mount would resolve the whole session up
  // front and throw away the laziness the resolver exists for.
  const feedRef = useRef(null);
  useEffect(() => {
    if (!clock.ready || typeof IntersectionObserver !== "function") return undefined;
    const root = feedRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const turn = Number(entry.target.getAttribute("data-turn"));
          if (Number.isInteger(turn)) requestTurn(turn);
        }
      },
      // A margin so a row is dated just before it arrives, and the reading is
      // there when the reader gets to it rather than popping in under them.
      { root: null, rootMargin: "120px 0px", threshold: 0 },
    );
    for (const el of root.querySelectorAll("[data-turn]")) io.observe(el);
    return () => io.disconnect();
  }, [clock.ready, requestTurn, feed.rows]);

  const rowKeys = useMemo(
    () => feed.rows.map((row, i) => (row.type === "ops" || row.type === "residual" ? row.items[0].id : row.item.id) + `:${i}`),
    [feed.rows],
  );

  // Which turn each row belongs to, in render order. Known from the feed alone,
  // so it does not move as the clock fills in: it is what a rung's turn range is
  // resolved against to find the rows the rung covers, and resolving that
  // against the DATED rows instead is what made every instant follow the reader
  // down the page.
  const turnStarts = useMemo(() => feed.rows.map((row) => rowTurnSpan(row)?.start ?? null), [feed.rows]);

  // Every row's clock reading, in render order. feed.rows IS render order —
  // partitionEpisodes only regroups it — so one pass feeds both the rail's
  // boundaries and the endpoint windows, and the two can never disagree about
  // which rung a row is in.
  const readings = useMemo(
    () =>
      feed.rows.map((row) => {
        if (!clock.ready) return null;
        const span = rowTurnSpan(row);
        return span ? clock.timeOf(span.start) : null;
      }),
    [feed.rows, clock.ready, clock.timeOf],
  );

  // Where the rail draws a capture instant, and which window each one opens —
  // see lib/traceEndpointCorroboration.js, which owns the rule.
  const boundaries = useMemo(() => windowBoundaries(readings, turnStarts), [readings, turnStarts]);
  const messageBoundaries = useMemo(
    () => messageBoundaryLayout(feed.rows, boundaries),
    [feed.rows, boundaries],
  );

  // One endpoint window per STRETCH of rows that shares it, keyed by the row
  // whose boundary opens it. Every row of a stretch shares the window, so
  // hanging the reading off each of them would repeat one set of events down the
  // page and imply each row in turn produced them; the boundary is where the
  // window actually begins, and is the only place it can be opened.
  //
  // Candidates come from the whole stretch rather than the opening row alone: a
  // file edited three rows down sits inside the same span of time and is exactly
  // as corroborable. Memoized because the walk reads tool-call blocks and the
  // matcher runs against its output — `clock.timeOf` changes identity precisely
  // when a turn gets dated, which is the only thing that can move a stretch.
  const windows = useMemo(() => {
    const map = new Map();
    if (!clock.ready || !endpointId) return map;
    for (const stretch of windowStretches(readings, turnStarts)) {
      map.set(stretch.from, {
        window: stretch.window,
        candidates: windowCandidates(feed.rows.slice(stretch.from, stretch.to + 1), turns),
      });
    }
    return map;
  }, [feed.rows, readings, turnStarts, turns, clock.ready, endpointId]);

  // Apply the carried range, once per arrival: every row whose span intersects
  // it lights up, and the first is scrolled to. Re-applying while the reader
  // scrolls would fight the capture below.
  const feedArrivalRef = useRef(-1);  // -1 so the cold mount counts as an arrival
  // The landing cannot be one-shot here. This feed's rows are rebuilt as its
  // async parts resolve (artifact groups, clock readings), which both moves
  // the target and re-keys it — so a landing computed at arrival converges on
  // a layout that no longer exists. Re-issue it on every rebuild for a short
  // window after arrival; landOnRow yields as soon as the reader scrolls.
  const landUntilRef = useRef(0);
  useEffect(() => {
    // Clearing the coordinate has to clear the marks. This feed is NOT
    // remounted when the trace changes, so an early return that leaves
    // focusKeys alone leaves a row in the next conversation wearing the last
    // one's mark. Same for a coordinate that lands on no row at all.
    if (!Number.isInteger(turnFocus) || !feed.rows.length) { setFocusKeys(null); return undefined; }
    // Restored by the cleanup: StrictMode tears an effect down and runs it
    // again, so consuming the arrival on the first pass and cancelling its
    // landing in the teardown left the second pass believing it had already
    // landed. This feed mounts fresh on arrival, so that was every time.
    const serviced = feedArrivalRef.current;
    if (feedArrivalRef.current !== turnFocusArrival) {
      feedArrivalRef.current = turnFocusArrival;
      landUntilRef.current = Date.now() + 2500;
    }
    const hits = [];
    feed.rows.forEach((row, i) => {
      const span = rowTurnSpan(row);
      if (span && turnFocusInSpan(turnFocus, span.start, span.end)) hits.push(i);
    });
    // Nothing intersects when the range falls in a stretch the extractors
    // claimed nothing for; fall back to the first row at or after it so the
    // reader still lands nearby rather than at the top.
    if (!hits.length) {
      const after = feed.rows.findIndex((row) => (rowTurnSpan(row)?.start ?? -1) >= turnFocus);
      if (after >= 0) hits.push(after);
    }
    if (!hits.length) { setFocusKeys(null); return undefined; }
    const keys = new Set(hits.map((i) => rowKeys[i]));
    setFocusKeys(keys);
    if (Date.now() > landUntilRef.current) return undefined;
    const cancel = landOnRow(() => rowRefs.current.get(rowKeys[hits[0]]), { align: "center" });
    // A frame having run is what makes the arrival really consumed; a
    // StrictMode teardown gets in before one and hands the ref back.
    let started = false;
    const mark = window.requestAnimationFrame(() => { started = true; });
    return () => {
      window.cancelAnimationFrame(mark);
      cancel();
      if (!started) feedArrivalRef.current = serviced;
    };
  }, [turnFocus, turnFocusArrival, feed.rows, rowKeys]);


  if (!feed.rows.length) {
    return (
      <div className={`py-[8px] ${META_CLASS} text-[color:var(--muted)]`}>
        No prompts, file work, research, queries, or publishing activity were recognized in this trace.
      </div>
    );
  }

  const refFor = (key) => (el) => {
    if (el) rowRefs.current.set(key, el);
    else rowRefs.current.delete(key);
  };

  // Bound per row, so a chip anywhere inside it resolves to a preview directly
  // beneath that row. Clicking the open chip again closes it.
  const previewFor = (key) => ({
    open: preview?.key === key ? preview.turn : null,
    request: (turn) =>
      setPreview((cur) => (cur && cur.key === key && cur.turn === turn ? null : { key, turn })),
  });

  const { coverage } = feed;
  // feedDomains() rather than the static table: a runtime lens mints its own feed
  // domain, and that domain is only known once the tenant's catalog resolves. Read
  // off the static map, a mounted lens's rows render their raw domain id as the lane
  // label instead of the lens's own name.
  const domains = feedDomains();
  const toggleWindow = (key) => setOpenWindow((cur) => (cur === key ? null : key));

  // The boundary drawn BEFORE a row, carrying whatever the endpoint was doing in
  // the window it opens. A boundary with no window — the rung open on the left, a
  // trace that names no endpoint, a window that held nothing — is still a real
  // observed capture, and renders as its time alone.
  const boundaryBefore = (index) => {
    const entries = [];
    if (!messageBoundaries.consumedBefore.has(index)) {
      const at = boundaries.before.get(index);
      if (at) entries.push({ at, sourceIndex: index });
    }
    const moved = messageBoundaries.movedBefore.get(index);
    if (moved) entries.push(moved);
    return entries.map(({ at, sourceIndex }, ordinal) => {
      const act = sourceIndex == null ? null : windows.get(sourceIndex) || null;
      return (
        <RailBoundary key={`${at.ts}:${ordinal}`} at={clockLabel(at.ts)}>
          {act ? (
            <EndpointWindow
              window={act.window}
              candidates={act.candidates}
              open={openWindow === act.window.key}
              onToggle={() => toggleWindow(act.window.key)}
            />
          ) : null}
        </RailBoundary>
      );
    });
  };

  // How far the endpoint reading got, said ONCE. Every refusal on the rail is
  // one window's answer, so a trace whose read was cut short renders the same
  // mark dozens of times and the reader has to hover one to learn that the
  // reading stopped — and still cannot tell whether it stopped here or
  // everywhere. Scored beside the other two readings of this feed, in their
  // shape, and only when something actually went unread: a reading that covered
  // every window is complete, and scoring a complete reading is noise.
  const { read, unread, failed } = corroboration.coverage;
  const endpointRead =
    unread > 0 || failed > 0 ? `${read} of ${read + unread + failed} windows read for session activity` : "";

  // The closing instant under the last dated row. It opens no window — the rung
  // it closes was opened by a boundary further up, and that is where its
  // activity is read.
  const boundaryClose = (index) =>
    !messageBoundaries.consumedClose && boundaries.closeIndex === index && Number.isFinite(boundaries.closeTs)
      ? <RailBoundary at={clockLabel(boundaries.closeTs)} />
      : null;
  return (
    <TraceTimeContext.Provider value={traceTimestamp}>
    <TraceTurnsContext.Provider value={turns}>
    <JumpToTurnContext.Provider value={onJumpToTurn}>
    <TurnClockContext.Provider value={clock.ready ? clock : null}>
    <EndpointCorroborationContext.Provider value={endpointId ? corroboration : null}>
    <div ref={feedRef} className="grid min-w-0 content-start">
      <div className={`sticky top-[var(--reading-switcher-h,0px)] z-[3] flex flex-wrap items-baseline gap-x-[8px] border-b border-[color:var(--line-soft)] bg-[color:var(--bg)] pb-[4px] pt-[2px] ${META_CLASS} text-[color:var(--muted)]`}>
        <span>
          {coverage.claimed} of {coverage.toolUses} tool calls classified
        </span>
        {/* Dating progress, beside the coverage score rather than in the rows:
            the work is per-scroll and asynchronous, and a spinner attached to
            individual rows would flicker down the page as the observer fires.
            aria-busy is the detectable marker (see dashboard/AGENTS.md), the
            pulsing dot is only the visual cue. */}
        {clock.busy ? (
          <>
            <span className="text-[color:var(--line-strong)]">·</span>
            <span className="inline-flex items-baseline gap-[5px] text-[color:var(--muted-soft)]" aria-busy="true">
              <span className="inline-block h-[5px] w-[5px] animate-pulse rounded-full bg-[color:var(--eyebrow)]" aria-hidden="true" />
              {clock.loadingPriors ? "finding earlier requests…" : `dating turns… ${clock.inFlight}`}
            </span>
          </>
        ) : clock.ready && clock.coverage.asked > 0 ? (
          <>
            <span className="text-[color:var(--line-strong)]">·</span>
            <span className="text-[color:var(--muted-soft)]">
              {`${clock.coverage.dated} of ${clock.coverage.asked} turns dated`}
            </span>
          </>
        ) : null}
        {/* Endpoint lookups report here for the same reason dating does: the
            work is per-window and asynchronous, and a marker on the row would
            flicker down the page as windows resolve. */}
        {corroboration.busy ? (
          <>
            <span className="text-[color:var(--line-strong)]">·</span>
            <span className="inline-flex items-baseline gap-[5px] text-[color:var(--muted-soft)]" aria-busy="true">
              <span className="inline-block h-[5px] w-[5px] animate-pulse rounded-full bg-[color:var(--eyebrow)]" aria-hidden="true" />
              reading session activity…
            </span>
          </>
        ) : endpointRead ? (
          <>
            <span className="text-[color:var(--line-strong)]">·</span>
            <span className="text-[color:var(--muted-soft)]">{endpointRead}</span>
          </>
        ) : null}
        {coverage.residual > 0 ? (
          <>
            <span className="text-[color:var(--line-strong)]">·</span>
            <button
              type="button"
              onClick={() => setExpandAllTick((n) => n + 1)}
              className="cursor-pointer border-b border-dotted border-[color:var(--line-strong)] text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--ink)]"
            >
              {`${coverage.residual} unclassified. Expand all`}
            </button>
          </>
        ) : null}
      </div>
      {episodes.map((ep, epIdx) => (
        <section key={ep.prompt ? rowKeys[ep.promptIndex] : `lead:${epIdx}`} className="relative min-w-0">
          {ep.prompt ? (
            <>
            {/* Outside the sticky wrapper, so the boundary scrolls away with the
                turns it separates rather than riding the pinned header — and so
                a window opened from it is never pinned over the rows it
                explains. That is what lets an episode header open a window at
                all; the reading it used to be denied sits just above it. */}
            {boundaryBefore(ep.promptIndex)}
            {/* The sticky element's containing block is the SECTION — that's
                what lets the header slide down it and release at the next
                episode. An intermediate tight wrapper would pin it in place. */}
            <div
              ref={refFor(rowKeys[ep.promptIndex])}
              data-episode-header=""
              // The observer only watches [data-turn]. Without this the prompt's
              // own turn is never requested, so its clock stays pending forever
              // and an episode header is the one beat that can never show a
              // time — silently, because a pending turn renders the same as a
              // turn with no reading.
              data-turn={ep.prompt.item.turnIndex ?? undefined}
              data-turn-focused={focusKeys?.has(rowKeys[ep.promptIndex]) ? "" : undefined}
              style={focusRing(rowKeys[ep.promptIndex])}
              className="sticky top-[calc(var(--reading-switcher-h,0px)+22px)] z-[2] min-w-0 bg-[color:var(--bg)]"
            >
              <TurnPreviewContext.Provider value={previewFor(rowKeys[ep.promptIndex])}>
                <PromptHeader item={ep.prompt.item} />
              </TurnPreviewContext.Provider>
            </div>
            {boundaryClose(ep.promptIndex)}
            </>
          ) : null}
          {/* Outside the sticky wrapper: a preview opened from the episode
              header is content, and sticking it to the top would cover the very
              rows it was opened to explain. */}
          {/* `ep.prompt &&` is load-bearing: a lead episode has promptIndex -1,
              so rowKeys[-1] is undefined and would match an absent preview's
              undefined key. */}
          {ep.prompt && preview?.key === rowKeys[ep.promptIndex] ? (
            <div className={`grid min-w-0 ${GUTTER_COLS}`}>
              <GutterCell kind="span" />
              <div className={`${INDENT} col-span-2 min-w-0 pb-[3px]`}>
                <TurnPreview turnIndex={preview.turn} />
              </div>
            </div>
          ) : null}
          {ep.rows.map(({ row, index }) => {
            const key = rowKeys[index];
            const gutterKind =
              row.type === "residual" ? "residual"
                : row.type === "prompt" ? "notice"
                  : row.type === "reply" ? "reply"
                    : "op";
            const lane = laneLabelOf(row, domains);
            const isBeat = row.type === "reply";
            return (
              <Fragment key={key}>
              {boundaryBefore(index)}
              <div
                ref={refFor(key)}
                data-turn={rowTurnSpan(row)?.start ?? undefined}
                data-turn-focused={focusKeys?.has(key) ? "" : undefined}
                style={focusRing(key)}
                className={`grid min-w-0 ${GUTTER_COLS}`}
              >
                {/* Nothing endpoint-shaped is drawn here. Everything a window
                    has to say lives on the boundary that opens it, which is the
                    one place it can be opened — so a mark on the rail always
                    means "there is something here to read". */}
                <GutterCell kind={gutterKind} />
                {isBeat ? null : (
                  <LaneCell label={lane} dim={row.type === "residual" || row.type === "prompt"} />
                )}
                {/* Spans the lane only when the lane cell is genuinely empty.
                    A reply carries no domain label, and claiming the span while
                    the cell rendered put four children in a three-column grid. */}
                <div className={isBeat ? "col-span-2 min-w-0" : "min-w-0"}>
                  <TurnPreviewContext.Provider value={previewFor(key)}>
                    {row.type === "residual" ? (
                      <ResidualRow row={row} expandAllTick={expandAllTick} />
                    ) : row.type === "prompt" ? (
                      <NoticeRow item={row.item} />
                    ) : row.type === "reply" ? (
                      <ReplyBeat item={row.item} />
                    ) : (
                      <OpsRow row={row} />
                    )}
                    {preview?.key === key ? (
                      <div className={`${INDENT} min-w-0`}>
                        <TurnPreview turnIndex={preview.turn} />
                      </div>
                    ) : null}
                  </TurnPreviewContext.Provider>
                </div>
              </div>
              {boundaryClose(index)}
              </Fragment>
            );
          })}
        </section>
      ))}
    </div>
    </EndpointCorroborationContext.Provider>
    </TurnClockContext.Provider>
    </JumpToTurnContext.Provider>
    </TraceTurnsContext.Provider>
    </TraceTimeContext.Provider>
  );
}
